import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Stack,
  Switch,
  Tooltip,
  InputAdornment,
  Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";
import api from "../../api";
import { adminHasPermission, adminHasAnyPermission } from "../../utils/adminPermissions";

const MASTER = "manage-roles-permissions";

const errorText = (err, fallback) => {
  const data = err?.response?.data;
  if (data?.errors) return Object.values(data.errors).flat().join(" ");
  return data?.message || fallback;
};

export default function PermissionsManagement() {
  const canToggle =
    adminHasPermission(MASTER) && adminHasAnyPermission(["toggle-permission-status", "manage-permissions"]);

  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState("all");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [confirm, setConfirm] = useState(null); // permission to deactivate
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("admin/rbac/permissions");
      setPermissions(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      toast.error(errorText(err, "Failed to load permissions."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const groups = useMemo(
    () => Array.from(new Set(permissions.map((p) => p.group || "Other"))).sort((a, b) => a.localeCompare(b)),
    [permissions]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return permissions.filter((p) => {
      if (group !== "all" && (p.group || "Other") !== group) return false;
      if (status === "active" && !p.is_active) return false;
      if (status === "inactive" && p.is_active) return false;
      if (q && !`${p.name} ${p.group || ""} ${p.description || ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [permissions, search, group, status]);

  useEffect(() => {
    setPage(0);
  }, [search, group, status]);

  const counts = useMemo(
    () => ({
      total: permissions.length,
      inactive: permissions.filter((p) => !p.is_active).length,
    }),
    [permissions]
  );

  const changeStatus = async (perm, active) => {
    setBusyId(perm.id);
    try {
      await api.patch(`admin/rbac/permissions/${perm.id}/status`, { is_active: active });
      setPermissions((prev) => prev.map((p) => (p.id === perm.id ? { ...p, is_active: active } : p)));
      toast.success(active ? `${perm.name} activated.` : `${perm.name} deactivated.`);
    } catch (err) {
      toast.error(errorText(err, "Failed to change permission status."));
    } finally {
      setBusyId(null);
      setConfirm(null);
    }
  };

  const pageRows = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 3, minHeight: "100vh", background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)" }}>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
        Permissions
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {counts.total} permissions, {counts.inactive} inactive. An inactive permission is denied for everyone except
        Super Admin, even on roles that still have it.
      </Typography>
      {!canToggle && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You can view permissions. Changing their status needs "{MASTER}" and "toggle-permission-status".
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search name, group or description"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ minWidth: 280 }}
          />
          <TextField select size="small" label="Group" value={group} onChange={(e) => setGroup(e.target.value)} sx={{ minWidth: 220 }}>
            <MenuItem value="all">All groups</MenuItem>
            {groups.map((g) => (
              <MenuItem key={g} value={g}>{g}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
        </Stack>

        {loading ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Permission</TableCell>
                    <TableCell>Group</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Roles</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.map((p) => (
                    <TableRow key={p.id} hover sx={{ opacity: p.is_active ? 1 : 0.6 }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} sx={{ fontFamily: "monospace" }}>
                          {p.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {p.group || "Other"}
                        {p.super_admin_only ? <Chip size="small" label="Super Admin only" sx={{ ml: 1 }} /> : null}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{p.description || "-"}</Typography>
                      </TableCell>
                      <TableCell align="right">{p.roles_count ?? 0}</TableCell>
                      <TableCell align="center">
                        <Tooltip title={canToggle ? (p.is_active ? "Deactivate" : "Activate") : ""}>
                          <span>
                            <Switch
                              size="small"
                              checked={!!p.is_active}
                              disabled={!canToggle || busyId === p.id}
                              onChange={(e) => (e.target.checked ? changeStatus(p, true) : setConfirm(p))}
                            />
                          </span>
                        </Tooltip>
                        <Chip
                          size="small"
                          color={p.is_active ? "success" : "default"}
                          label={p.is_active ? "Active" : "Inactive"}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {pageRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">No permissions match.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </>
        )}
      </Paper>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Deactivate permission?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ fontFamily: "monospace", mb: 1 }}>{confirm?.name}</Typography>
          <Typography variant="body2">
            {confirm?.roles_count
              ? `${confirm.roles_count} role(s) have it. Users on those roles lose access to it right away (Super Admin keeps it).`
              : "No role has it right now."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => changeStatus(confirm, false)}>
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
